/**
 * DashboardCanvas Component (Dashboards feature)
 *
 * A WEB-STYLE clone of the report `ReportCanvas` (apps/web/src/components/reports/
 * ReportCanvas.tsx) — same 12-column dnd-kit grid + widget tooling, but rendered as
 * a CONTINUOUS, FULL-WIDTH web surface instead of a fixed A4 paper sheet:
 *
 *   - NO fixed `paperWidthPx` — the surface fills the FULL width of the available
 *     canvas area. The grid's pixel pitch is derived from the MEASURED container
 *     width (ResizeObserver), so 12-col widgets span the whole width and reflow
 *     responsively as the viewport / sidebars change.
 *   - NO A4 `minHeight` clamp — height is CONTINUOUS, growing with content (the grid
 *     reports its own min-height from the bottom-most widget).
 *   - NO page-break markers, NO centered paper card, NO heavy paper shadow / rounded
 *     paper boundary — it reads as a flush web dashboard, not a printable page.
 *   - Keeps the dot-grid guides (edit mode), the empty state, the CanvasGrid mount,
 *     and ALL widget positioning + dnd (move/resize/drop).
 *
 * Everything paper-agnostic is REUSED BY IMPORT from components/reports/ (CanvasGrid,
 * WidgetRenderer, reportDnd math, gridUtils, ReportDndProvider) — those files are NOT
 * modified. The ONE report seam that bakes in the A4 width is `useCanvasDropTarget`
 * (it reads getGridDimensions(format).contentWidthPx), so the drop-target tracking is
 * reimplemented inline here against the MEASURED width via the pure `pointerToGridCell`.
 */

import { useRef, useMemo, useCallback, useEffect, useState } from "react";
import { MousePointerClick, Image as ImageIcon, Hash, TrendingUp, BarChart3, Table, PieChart, Newspaper } from "lucide-react";
import { useDroppable } from "@dnd-kit/core";
import { WidgetRenderer } from "../reports/widgets/WidgetRenderer";
import { CanvasGrid } from "../reports/CanvasGrid";
import { useViewportBreakpoint, type ViewportBreakpoint } from "../../lib/useViewportBreakpoint";
import type { WidgetConfig } from "../../lib/templateUtils";
import type { WidgetData } from "../../lib/widgetData";
import { GRID_COLUMNS } from "../../lib/gridUtils";
import { getGridDimensions } from "@provenleap/types/report-rendering";
import type { ReportFormat } from "@provenleap/types/report-rendering";
import { useReportDnd, CANVAS_DROPPABLE_ID } from "../reports/ReportDndProvider";
import {
  applyMoveDelta,
  pointerToGridCell,
  type GridMetrics,
  type GridCell,
} from "../../lib/reportDnd";

// ── Responsive Grid Helpers (verbatim from ReportCanvas — paper-agnostic) ────────

function getResponsiveWidgets(
  widgets: WidgetConfig[],
  breakpoint: ViewportBreakpoint
): WidgetConfig[] {
  if (breakpoint === "desktop") return widgets;

  if (breakpoint === "mobile") {
    return widgets.map((w, i) => ({
      ...w,
      position: { ...w.position, row: i, col: 0, colSpan: 12 },
    }));
  }

  let currentRow = 0;
  let currentCol = 0;
  return widgets.map((w) => {
    const cappedSpan = Math.min(w.position.colSpan, 6);
    if (currentCol + cappedSpan > 12) {
      currentRow++;
      currentCol = 0;
    }
    const adapted = {
      ...w,
      position: {
        ...w.position,
        row: currentRow,
        col: currentCol,
        colSpan: cappedSpan,
      },
    };
    currentCol += cappedSpan;
    return adapted;
  });
}

// ── Grid Guides ─────────────────────────────────────────────────────
// Same dot/column guides as the report canvas, but they fill the full-width
// surface (no fixed paper padding).

function GridGuides({ gapPx }: { gapPx: number }) {
  return (
    <div className="absolute inset-0 pointer-events-none z-0" style={{ padding: "24px" }}>
      <div className="relative w-full h-full" style={{ display: "grid", gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`, gap: gapPx }}>
        {Array.from({ length: GRID_COLUMNS - 1 }, (_, i) => (
          <div
            key={`guide-${i}`}
            data-testid={`grid-guide-${i}`}
            className="border-r border-dashed border-muted-foreground/10 h-full"
          />
        ))}
        <div />
      </div>
    </div>
  );
}

// ── Empty-state ghost skeleton layout (verbatim from ReportCanvas) ──────────────

interface GhostItem {
  type: string;
  label: string;
  icon: typeof Hash;
  position: { row: number; col: number; colSpan: number; rowSpan: number };
  span: number;
  h: number;
  opacity: number;
}

const GHOST_ITEMS: GhostItem[] = [
  { type: "cover-page", label: "Cover page", icon: ImageIcon, position: { row: 0, col: 0, colSpan: 12, rowSpan: 3 }, span: 12, h: 96, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 0, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 3, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 6, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 9, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "line-chart", label: "Line chart", icon: TrendingUp, position: { row: 4, col: 0, colSpan: 6, rowSpan: 2 }, span: 6, h: 140, opacity: 1 },
  { type: "bar-chart", label: "Bar chart", icon: BarChart3, position: { row: 4, col: 6, colSpan: 6, rowSpan: 2 }, span: 6, h: 140, opacity: 1 },
  { type: "data-table", label: "Data table", icon: Table, position: { row: 6, col: 0, colSpan: 12, rowSpan: 3 }, span: 12, h: 130, opacity: 1 },
  { type: "pie-chart", label: "Pie chart", icon: PieChart, position: { row: 9, col: 0, colSpan: 6, rowSpan: 2 }, span: 6, h: 120, opacity: 1 },
  { type: "top-posts", label: "Top posts", icon: Newspaper, position: { row: 9, col: 6, colSpan: 6, rowSpan: 2 }, span: 6, h: 120, opacity: 1 },
];

function EmptyGhostLayout({
  onEmptyAddAt,
  onEmptyQuickAdd,
  onEmptyDrop,
}: {
  onEmptyAddAt?: DashboardCanvasProps["onEmptyAddAt"];
  onEmptyQuickAdd?: DashboardCanvasProps["onEmptyQuickAdd"];
  onEmptyDrop?: DashboardCanvasProps["onEmptyDrop"];
}) {
  const handleClick = (item: GhostItem) => {
    if (onEmptyAddAt) {
      onEmptyAddAt(item.type, item.position);
    } else if (onEmptyQuickAdd && (item.type === "kpi" || item.type === "line-chart")) {
      onEmptyQuickAdd(item.type);
    }
  };

  return (
    <div
      data-testid="dashboard-canvas-empty"
      className="flex flex-col gap-3"
      onDragOver={onEmptyDrop ? (e) => e.preventDefault() : undefined}
      onDrop={
        onEmptyDrop
          ? (e) => {
              e.preventDefault();
              onEmptyDrop(e.nativeEvent);
            }
          : undefined
      }
    >
      <div className="mb-4 text-center text-[17px]" style={{ color: "hsl(var(--foreground))" }}>
        Click a placeholder to add it, or drag widgets from the palette, or press{" "}
        <kbd className="text-sm font-medium text-foreground border border-input rounded-[9px] px-2.5 py-1 bg-muted/40 ml-2">
          <span className="relative top-px text-base">⌘</span> + K
        </kbd>
      </div>
      <div className="grid grid-cols-12 gap-3">
        {GHOST_ITEMS.map((item, i) => {
          const Icon = item.icon;
          return (
            <button
              key={`${item.type}-${i}`}
              type="button"
              onClick={() => handleClick(item)}
              data-testid={`ghost-${item.type}-${i}`}
              aria-label={`Add ${item.label}`}
              className="group/ghost flex flex-col items-center justify-center gap-1.5 rounded-[14px] bg-muted/10 hover:!opacity-100 hover:text-foreground hover:bg-muted/40 transition-all cursor-pointer"
              style={{
                gridColumn: `span ${item.span} / span ${item.span}`,
                height: item.h,
                border: "1px dashed hsl(214 25% 62%)",
                color: "hsl(var(--foreground))",
                opacity: item.opacity,
              }}
            >
              <Icon size={17} className="shrink-0" />
              <span className="text-[17px] text-center">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Full-width drop target ───────────────────────────────────────────
// Parallel to the report's `useCanvasDropTarget`, but takes the MEASURED grid
// `contentWidthPx` (not the fixed A4 width getGridDimensions bakes in) so the snap
// cell tracks the cursor across the full-width grid. Reuses CANVAS_DROPPABLE_ID and
// the pure transform-safe `pointerToGridCell` (both imported, unmodified).

function useFullWidthDropTarget(params: {
  enabled: boolean;
  scale: number;
  metrics: GridMetrics;
  activeSpan: { colSpan: number; rowSpan: number } | null;
}): {
  setNodeRef: (el: HTMLElement | null) => void;
  getHoverCell: () => GridCell | null;
} {
  const { enabled, scale, metrics, activeSpan } = params;
  const { setNodeRef: setDroppableRef } = useDroppable({
    id: CANVAS_DROPPABLE_ID,
    disabled: !enabled,
  });

  const elRef = useRef<HTMLElement | null>(null);
  const setNodeRef = useCallback(
    (el: HTMLElement | null) => {
      elRef.current = el;
      setDroppableRef(el);
    },
    [setDroppableRef],
  );

  const hoverCellRef = useRef<GridCell | null>(null);
  const getHoverCell = useCallback(() => hoverCellRef.current, []);

  // Keep the latest metrics/scale/span readable from the window listener without
  // re-subscribing on every render (metrics is a fresh object each render).
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const spanRef = useRef(activeSpan);
  spanRef.current = activeSpan;

  useEffect(() => {
    if (!enabled || !activeSpan) {
      hoverCellRef.current = null;
      return;
    }
    const onMove = (e: PointerEvent) => {
      const el = elRef.current;
      const span = spanRef.current;
      if (!el || !span) return;
      const rect = el.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        hoverCellRef.current = null;
        return;
      }
      hoverCellRef.current = pointerToGridCell(
        { x: e.clientX, y: e.clientY },
        rect,
        scaleRef.current,
        metricsRef.current,
        span,
      );
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
    // Re-subscribe only when drag activity (enabled/span presence) flips; the live
    // metrics/scale are read via refs so a width re-measure doesn't drop the listener.
  }, [enabled, activeSpan]);

  return { setNodeRef, getHoverCell };
}

// ── Component ───────────────────────────────────────────────────────

export interface DashboardCanvasProps {
  widgets: WidgetConfig[];
  widgetData?: Record<string, WidgetData>;
  selectedWidgetId?: string | null;
  onWidgetSelect?: (widgetId: string) => void;
  onWidgetDelete?: (widgetId: string) => void;
  onWidgetDuplicate?: (widgetId: string) => void;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
  editable?: boolean;
  reportDateRangeStart?: string;
  reportDateRangeEnd?: string;
  selectedClientName?: string;
  selectedClientLogoUrl?: string;
  /** Format — drives ONLY the rowHeight/gap grid pitch; the WIDTH is measured. */
  format?: ReportFormat;
  onWidgetMove?: (widgetId: string, cell: { row: number; col: number }) => void;
  onWidgetResize?: (widgetId: string, colSpan: number, rowSpan: number) => void;
  zoomLevel?: number;
  onEmptyQuickAdd?: (type: "kpi" | "line-chart" | "data-table" | "heading") => void;
  onEmptyAddAt?: (
    type: string,
    position: { row: number; col: number; colSpan: number; rowSpan: number },
  ) => void;
  onEmptyDrop?: (e: DragEvent) => void;
  /**
   * Explicit canvas width override (px, unscaled). The dashboard editor host
   * measures the canvas area and threads it here so the grid pitch matches the
   * surface BEFORE the surface paints (no first-frame jump). When absent, the
   * canvas measures its own rendered width via ResizeObserver (read-only hub view).
   */
  measuredWidthPx?: number;
}

export function DashboardCanvas({
  widgets,
  widgetData,
  selectedWidgetId,
  onWidgetSelect,
  onWidgetDelete,
  onWidgetDuplicate,
  onAutoResize,
  editable = false,
  reportDateRangeStart,
  reportDateRangeEnd,
  selectedClientName,
  selectedClientLogoUrl,
  format = "document",
  onWidgetMove,
  onWidgetResize,
  zoomLevel = 100,
  onEmptyQuickAdd,
  onEmptyAddAt,
  onEmptyDrop,
  measuredWidthPx,
}: DashboardCanvasProps) {
  const breakpoint = useViewportBreakpoint();
  const isDesktop = breakpoint === "desktop";
  const effectiveEditable = editable && isDesktop;

  // Format-aware grid pitch (rowHeight/gap only) — the WIDTH comes from the
  // measured surface so widgets span the full available width, not the A4 width.
  const dims = useMemo(() => getGridDimensions(format), [format]);
  const rowHeightPx = dims.rowHeightPx;
  const gapPx = dims.gapPx;

  // ── Measure the surface width (ResizeObserver) ──────────────────────
  // A measured ref feeds the GridMetrics so the 12-col grid recalculates for ANY
  // width and reflows on resize. The host's explicit `measuredWidthPx` wins when
  // provided (no first-frame width jump); otherwise we self-measure.
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [selfMeasuredWidth, setSelfMeasuredWidth] = useState<number>(0);
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => setSelfMeasuredWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Effective grid width: explicit host width > self-measured > A4 fallback (so
  // the FIRST paint and the jsdom/test path — where ResizeObserver may be absent —
  // still position widgets sensibly instead of collapsing to 0).
  const contentWidthPx =
    (measuredWidthPx && measuredWidthPx > 0
      ? measuredWidthPx
      : selfMeasuredWidth > 0
        ? selfMeasuredWidth
        : dims.contentWidthPx);

  const responsiveWidgets = useMemo(
    () => getResponsiveWidgets(widgets, breakpoint),
    [widgets, breakpoint]
  );

  // Canvas background click — deselect (only when clicking empty area).
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest("[data-testid='dashboard-canvas-grid']") === e.target) {
      onWidgetSelect?.("");
    }
  }, [onWidgetSelect]);

  const canvasPadding = 0; // flush web surface — no printable-margin inset
  const scale = zoomLevel / 100;

  // Shared grid metrics for all positioned-grid math (placeholder, move, resize),
  // keyed on the MEASURED width.
  const gridMetrics = useMemo<GridMetrics>(
    () => ({ contentWidthPx, rowHeightPx, gapPx, paddingPx: canvasPadding }),
    [contentWidthPx, rowHeightPx, gapPx],
  );

  // ── dnd-kit canvas DnD (reused from the report path, full-width metrics) ──────
  const { activeDrag, registerHoverCellGetter, registerMoveHandler } = useReportDnd();
  const activeSpan = activeDrag ? { colSpan: activeDrag.colSpan, rowSpan: activeDrag.rowSpan } : null;
  const dropTarget = useFullWidthDropTarget({
    enabled: effectiveEditable,
    scale,
    metrics: gridMetrics,
    activeSpan,
  });
  useEffect(() => {
    registerHoverCellGetter(dropTarget.getHoverCell);
  }, [registerHoverCellGetter, dropTarget.getHoverCell]);

  // Widget MOVE — same delta→cell snap as the report canvas, full-width metrics.
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;
  const handleWidgetMoveDelta = useCallback(
    (widgetId: string, delta: { x: number; y: number }) => {
      const w = widgetsRef.current.find((x) => x.id === widgetId);
      if (!w || w.type === "cover-page") return; // cover-page is pinned
      const cell = applyMoveDelta(
        { row: w.position.row, col: w.position.col },
        delta,
        scale,
        gridMetrics,
        { colSpan: w.position.colSpan, rowSpan: w.position.rowSpan ?? 1 },
      );
      if (cell.row !== w.position.row || cell.col !== w.position.col) {
        onWidgetMove?.(widgetId, cell);
      }
    },
    [scale, gridMetrics, onWidgetMove],
  );
  useEffect(() => {
    registerMoveHandler(handleWidgetMoveDelta);
  }, [registerMoveHandler, handleWidgetMoveDelta]);

  const handleResizeCommit = useCallback(
    (widgetId: string, colSpan: number, rowSpan: number) => {
      onWidgetResize?.(widgetId, colSpan, rowSpan);
    },
    [onWidgetResize],
  );

  // The dot-grid + grid-cell math need a real pixel width. The grid CONTENT height
  // is continuous (grows with widgets) — CanvasGrid reports its own min-height from
  // the bottom-most widget; here we use a small floor so an empty/short dashboard
  // still has a usable drop area.
  const minContentHeight = 320;

  // ── Empty canvas state ───────────────────────────────────────────────
  if (widgets.length === 0) {
    return (
      <div
        ref={surfaceRef}
        data-testid="dashboard-canvas-wrapper"
        className="report-surface relative w-full bg-transparent"
        style={{ minHeight: minContentHeight }}
      >
        <div
          ref={effectiveEditable ? dropTarget.setNodeRef : undefined}
          data-testid="dashboard-canvas-grid"
          className="min-h-[320px]"
          aria-label="Dashboard canvas drop zone"
          style={effectiveEditable ? { position: "relative" } : undefined}
        >
          {effectiveEditable ? (
            <EmptyGhostLayout onEmptyAddAt={onEmptyAddAt} onEmptyQuickAdd={onEmptyQuickAdd} onEmptyDrop={onEmptyDrop} />
          ) : (
            <div
              data-testid="dashboard-canvas-empty"
              className="flex flex-col items-center justify-center py-24 text-muted-foreground"
            >
              <MousePointerClick size={32} className="mb-3 text-muted-foreground/40" />
              <p className="text-center text-base">This dashboard has no widgets yet</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={surfaceRef}
      data-testid="dashboard-canvas-wrapper"
      className="relative w-full bg-transparent"
      onClick={handleCanvasClick}
    >
      {/* Mobile/tablet: read-only message */}
      {!isDesktop && editable && (
        <div
          data-testid="edit-on-desktop-banner"
          className="mb-3 rounded-lg bg-muted px-4 py-2 text-sm text-muted-foreground text-center"
        >
          Edit layout on desktop for the full editing experience.
        </div>
      )}

      {/* NO page-break markers — a dashboard is a continuous web surface. */}

      {/* Grid guides in edit mode */}
      {effectiveEditable && <GridGuides gapPx={gapPx} />}

      {/* dnd-kit canvas droppable wrapping the positioned CanvasGrid. The wrapper
          fills the full-width surface and IS the drop target (setNodeRef). */}
      <div
        ref={effectiveEditable ? dropTarget.setNodeRef : undefined}
        data-testid="dashboard-canvas-grid"
        aria-label="Dashboard canvas drop zone"
        style={effectiveEditable ? { position: "relative", minHeight: minContentHeight } : undefined}
      >
        <CanvasGrid
          widgets={responsiveWidgets}
          metrics={gridMetrics}
          minHeightPx={minContentHeight}
          scale={scale}
          editable={effectiveEditable}
          selectedWidgetId={selectedWidgetId}
          movingWidgetId={null}
          onWidgetResize={handleResizeCommit}
          onWidgetSelect={onWidgetSelect}
          onWidgetDelete={onWidgetDelete}
          onWidgetDuplicate={onWidgetDuplicate}
          renderWidget={(widget) => (
            <WidgetRenderer
              widget={widget}
              widgetData={widgetData}
              onAutoResize={onAutoResize}
              reportDateRangeStart={reportDateRangeStart}
              reportDateRangeEnd={reportDateRangeEnd}
              selectedClientName={selectedClientName}
              selectedClientLogoUrl={selectedClientLogoUrl}
              format={format}
            />
          )}
        />
      </div>
    </div>
  );
}
